import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Clock, IdCard, Loader2, ScanFace, ShieldCheck, UserCheck } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const IMG = (id, which) => `${process.env.REACT_APP_BACKEND_URL}/api/staff/identity/${id}/image/${which}`;
const STATE_LABEL = {
  pending_review: 'Waiting for review', verified: 'Verified',
  rejected: 'Rejected', photo_requested: 'Better photo asked for',
};
const DETAIL_ROWS = [
  ['Name on ID', 'full_name'], ['ID number', 'id_number'], ['Date of birth', 'date_of_birth'],
  ['ID expiry', 'id_expiry'], ['Lives at', 'residential_address'], ['Town', 'town'],
];

export const IdentityQueueView = () => {
  const [status, setStatus] = useState('pending_review');
  const [data, setData] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [tab, setTab] = useState('photo');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api.get('/staff/identity/queue', { params: { status } })
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || 'Could not load the verification queue'));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const openReview = (row) => { setReviewing(row); setTab('photo'); setReason(''); };
  const isClearCut = (row) => !(row.mismatches || []).length && row.liveness?.status === 'confirmed';

  const decide = async (row, decision, reasonText) => {
    if (decision !== 'verified' && reasonText.trim().length < 5) {
      return toast.error('Tell the customer what to do next');
    }
    setBusy(row.verification_id);
    try {
      await api.post(`/staff/identity/${row.verification_id}/decision`, { decision, reason: reasonText.trim() });
      toast.success(decision === 'verified' ? 'Customer verified' : 'Decision recorded');
      if (reviewing?.verification_id === row.verification_id) { setReviewing(null); setReason(''); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Decision failed'); }
    finally { setBusy(null); }
  };

  const quickApprove = (row) => {
    if (!window.confirm(`Approve ${row.subject_name || row.full_name}'s ID? The photo matched, with no mismatches and a confirmed selfie check.`)) return;
    decide(row, 'verified', '');
  };

  if (!data) return <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="identity-queue-loading">
    <Loader2 className="h-4 w-4 animate-spin" /> Loading ID verifications…</p>;

  return (
    <div className="space-y-5" data-testid="identity-queue-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2>Customer ID verification</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Step through the photo, selfie and details, then approve or tell the customer what to fix.
          </p>
        </div>
        <label className="care-label">Showing
          <select className="care-select" value={status} onChange={e => setStatus(e.target.value)} data-testid="identity-queue-filter">
            {data.statuses.map(s => <option key={s} value={s}>{STATE_LABEL[s] || s} · {data.counts?.[s] ?? 0}</option>)}
          </select>
        </label>
      </div>

      {data.verifications.length === 0 && (
        <p className="care-empty" data-testid="identity-queue-empty">Nothing in this state.</p>
      )}

      <div className="space-y-3">
        {data.verifications.map(row => (
          <article className="care-record flex flex-wrap items-center justify-between gap-3" key={row.verification_id} data-testid={`identity-row-${row.verification_id}`}>
            <div>
              <strong data-testid={`identity-name-${row.verification_id}`}>{row.subject_name || row.full_name}</strong>
              <p className="text-xs text-muted-foreground">
                {row.id_type_label} · {row.captured_by === 'counter' ? 'captured at the counter' : 'sent from the portal'} ·{' '}
                {row.submitted_at ? new Date(row.submitted_at).toLocaleString() : '—'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`care-kyc-badge care-kyc-${row.status === 'verified' ? 'verified' : row.status === 'rejected' ? 'rejected' : 'pending_review'}`}
                data-testid={`identity-state-${row.verification_id}`}>
                {STATE_LABEL[row.status] || row.status}
              </span>
              {row.status === 'pending_review' && isClearCut(row) && (
                <Button size="sm" variant="ghost" className="text-emerald-700" disabled={busy === row.verification_id}
                  onClick={() => quickApprove(row)} data-testid={`identity-quick-approve-${row.verification_id}`}>
                  {busy === row.verification_id ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={15} className="mr-1.5" />}
                  Quick approve
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => openReview(row)} data-testid={`identity-review-${row.verification_id}`}>
                <ShieldCheck size={15} className="mr-2" /> Review this ID
              </Button>
            </div>
          </article>
        ))}
      </div>

      <Dialog open={!!reviewing} onOpenChange={(o) => { if (!o) { setReviewing(null); setReason(''); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="identity-review-dialog"
          closeProps={{ 'data-testid': 'identity-review-close' }}>
          <DialogHeader>
            <DialogTitle>{reviewing?.subject_name || reviewing?.full_name}</DialogTitle>
            <DialogDescription>
              {reviewing?.id_type_label} · {reviewing?.captured_by === 'counter' ? 'captured at the counter' : 'sent from the portal'}
            </DialogDescription>
          </DialogHeader>
          {reviewing && (
            <Tabs value={tab} onValueChange={setTab} data-testid="identity-review-tabs">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="photo" data-testid="identity-tab-photo"><IdCard size={14} className="mr-1.5" /> ID photo</TabsTrigger>
                <TabsTrigger value="selfie" data-testid="identity-tab-selfie"><ScanFace size={14} className="mr-1.5" /> Selfie</TabsTrigger>
                <TabsTrigger value="details" data-testid="identity-tab-details">Details</TabsTrigger>
                <TabsTrigger value="decision" data-testid="identity-tab-decision"><UserCheck size={14} className="mr-1.5" /> Decision</TabsTrigger>
              </TabsList>

              <TabsContent value="photo" data-testid="identity-tabpanel-photo">
                <img src={IMG(reviewing.verification_id, 'id')} alt="Identity document"
                  className="w-full rounded-lg border object-contain bg-white" style={{ maxHeight: 420 }}
                  data-testid={`identity-image-id-${reviewing.verification_id}`} />
              </TabsContent>

              <TabsContent value="selfie" className="space-y-3" data-testid="identity-tabpanel-selfie">
                {reviewing.has_selfie && (
                  <img src={IMG(reviewing.verification_id, 'selfie')} alt="Customer selfie"
                    className="w-full rounded-lg border object-contain bg-white" style={{ maxHeight: 320 }}
                    data-testid={`identity-image-selfie-${reviewing.verification_id}`} />
                )}
                {reviewing.selfie_frames > 1 && (
                  <div className="flex gap-2" data-testid={`identity-frames-${reviewing.verification_id}`}>
                    {['selfie', 'selfie2', 'selfie3'].slice(0, reviewing.selfie_frames).map((which, i) => (
                      <figure key={which} className="flex-1 space-y-1">
                        <img src={IMG(reviewing.verification_id, which)} alt={`Liveness frame ${i + 1}`}
                          className="w-full rounded-lg border object-cover bg-white" style={{ maxHeight: 150 }}
                          data-testid={`identity-frame-${i + 1}-${reviewing.verification_id}`} />
                        <figcaption className="text-[11px] text-muted-foreground">Frame {i + 1}</figcaption>
                      </figure>
                    ))}
                  </div>
                )}
                {reviewing.liveness && (
                  <div className={reviewing.liveness.status === 'confirmed' ? 'care-note' : 'care-warning'}
                    data-testid={`identity-liveness-${reviewing.verification_id}`}>
                    <p className="flex items-center gap-2 font-medium">
                      <ScanFace size={15} />
                      {reviewing.liveness.status === 'confirmed' ? 'Liveness confirmed'
                        : reviewing.liveness.status === 'unavailable' ? 'Liveness check unavailable'
                        : 'Liveness not confirmed'}
                    </p>
                    <p className="text-sm">{reviewing.liveness.reason}</p>
                    {reviewing.liveness.assessment?.notes && (
                      <p className="text-xs text-muted-foreground">{reviewing.liveness.assessment.notes}</p>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="details" className="space-y-3" data-testid="identity-tabpanel-details">
                {(reviewing.mismatches || []).length > 0 && (
                  <p className="care-warning" data-testid={`identity-mismatch-count-${reviewing.verification_id}`}>
                    <AlertTriangle size={14} className="inline mr-1.5 align-text-bottom" />
                    {reviewing.mismatches.length} field{reviewing.mismatches.length === 1 ? '' : 's'} flagged below don't match what we read from the ID photo.
                  </p>
                )}
                <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2" data-testid={`identity-fields-${reviewing.verification_id}`}>
                  {DETAIL_ROWS.map(([label, key]) => {
                    const mismatch = (reviewing.mismatches || []).find(m => m.field === key);
                    return (
                      <div key={key} className={`verify-detail-row${mismatch ? ' has-mismatch' : ''}`}
                        data-testid={`identity-detail-${key}-${reviewing.verification_id}`}>
                        <dt className="flex items-center gap-1.5">
                          {mismatch && <AlertTriangle size={13} className="text-amber-600 shrink-0" />}
                          {label}
                        </dt>
                        <dd className="break-words">
                          {reviewing[key] || '—'}
                          {mismatch && <span className="verify-detail-mismatch-note">ID photo reads "{mismatch.read}"</span>}
                        </dd>
                      </div>
                    );
                  })}
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground min-w-28">Phone</dt>
                    <dd className="break-words">{reviewing.phone || '—'}{reviewing.phone_confirmed ? ' (confirmed)' : ''}</dd>
                  </div>
                </dl>
              </TabsContent>

              <TabsContent value="decision" className="space-y-3" data-testid="identity-tabpanel-decision">
                {reviewing.status === 'verified' ? (
                  <p className="text-sm text-muted-foreground" data-testid="identity-already-verified">
                    <CheckCircle2 size={15} className="inline mr-1.5 text-emerald-600" />
                    Verified{reviewing.reviewed_by_name ? ` by ${reviewing.reviewed_by_name}` : ''}.
                  </p>
                ) : (
                  <>
                    <Textarea value={reason} onChange={e => setReason(e.target.value)}
                      placeholder="What should the customer do next? They see this message."
                      data-testid={`identity-reason-${reviewing.verification_id}`} />
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" disabled={busy === reviewing.verification_id} onClick={() => decide(reviewing, 'verified', reason)}
                        data-testid={`identity-approve-${reviewing.verification_id}`}>
                        <CheckCircle2 size={15} className="mr-2" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy === reviewing.verification_id}
                        onClick={() => decide(reviewing, 'photo_requested', reason)}
                        data-testid={`identity-photo-${reviewing.verification_id}`}>
                        <Clock size={15} className="mr-2" /> Ask for a better photo
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" disabled={busy === reviewing.verification_id}
                        onClick={() => decide(reviewing, 'rejected', reason)}
                        data-testid={`identity-reject-${reviewing.verification_id}`}>Reject</Button>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
